import React, { Suspense, ComponentType } from 'react';
import { useTenant } from '@shared/providers/TenantContext';
import { moduleRegistry, modulePath, DEFAULT_TENANT_KEY } from './ModuleRegistry';

/**
 * Module Loader Component
 *
 * Renders the component registered at `module/component`, preferring the
 * current tenant's override when one exists.
 *
 * `notFound` is what renders when nothing is registered at all. Leaving it
 * undefined renders nothing rather than leaking a developer string into the UI.
 */

interface ModuleLoaderProps {
  module: string;
  component: string;
  fallback?: React.ReactNode;
  notFound?: React.ReactNode;
  props?: Record<string, unknown>;
}

export const ModuleLoader: React.FC<ModuleLoaderProps> = ({
  module,
  component,
  fallback = null,
  notFound = null,
  props = {},
}) => {
  const { tenant } = useTenant();
  const path = modulePath(module, component);
  const Component = moduleRegistry.getComponent(path, tenant || DEFAULT_TENANT_KEY);

  if (!Component) {
    // A missing registration is a wiring bug, not a user-facing state.
    console.error(`[ModuleLoader] No component registered for "${path}" (tenant: ${tenant})`);
    return <>{notFound}</>;
  }

  return (
    <Suspense fallback={fallback}>
      <Component {...props} />
    </Suspense>
  );
};

/**
 * Higher-order component to wrap a component with tenant override support.
 * Falls back to `BaseComponent` whenever the tenant has no override.
 */
export function withTenantOverride<P extends object>(
  module: string,
  componentName: string,
  BaseComponent: ComponentType<P>
): ComponentType<P> {
  return function TenantAwareComponent(props: P) {
    const { tenant } = useTenant();
    const tenantKey = tenant || DEFAULT_TENANT_KEY;
    const path = modulePath(module, componentName);

    if (!moduleRegistry.hasTenantOverride(path, tenantKey)) {
      return <BaseComponent {...props} />;
    }

    const OverrideComponent = moduleRegistry.getComponent(path, tenantKey);
    if (!OverrideComponent) {
      return <BaseComponent {...props} />;
    }

    return (
      <Suspense fallback={<BaseComponent {...props} />}>
        <OverrideComponent {...(props as Record<string, unknown>)} />
      </Suspense>
    );
  };
}

export default ModuleLoader;
